import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const ChefSignUp = ({ onLogin }) => {
  const navigate = useNavigate();
  const [signupType, setSignupType] = useState('chef'); // 'chef' or 'bartender'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    safeserv_cert_number: ''
  });
  const [licenseFile, setLicenseFile] = useState(null);
  const [llcFile, setLlcFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (signupType === 'chef') {
        // Chef signup
        const response = await axios.post('/auth/signup', {
          ...formData,
          role: 'chef'
        });
        
        onLogin(response.data.user, response.data.token);
        navigate('/chef/dashboard');
      } else {
        // Bartender signup with file uploads
        if (!licenseFile) {
          setError('Bartender license file is required');
          setLoading(false);
          return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('password', formData.password);
        formDataToSend.append('phone', formData.phone || '');
        formDataToSend.append('license_file', licenseFile);
        if (llcFile) {
          formDataToSend.append('llc_file', llcFile);
        }

        const response = await axios.post('/bartender/signup', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        onLogin(response.data.user, response.data.token);
        alert(response.data.message);
        navigate('/bartender/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg" data-testid="chef-signup-form">
        <div>
          <h2 className="text-center text-3xl font-bold text-gray-900">
            {signupType === 'chef' ? 'Become a Chef 👨‍🍳' : 'Become a Bartender 🍸'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {signupType === 'chef' ? 'Join our platform and start cooking!' : 'Join as a mobile bartender!'}
          </p>
        </div>

        {/* Toggle between Chef and Bartender */}
        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
          <button
            type="button"
            onClick={() => setSignupType('chef')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition ${
              signupType === 'chef'
                ? 'bg-orange-600 text-white shadow'
                : 'bg-transparent text-gray-700 hover:bg-gray-200'
            }`}
          >
            👨‍🍳 Chef
          </button>
          <button
            type="button"
            onClick={() => setSignupType('bartender')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition ${
              signupType === 'bartender'
                ? 'bg-purple-600 text-white shadow'
                : 'bg-transparent text-gray-700 hover:bg-gray-200'
            }`}
          >
            🍸 Bartender
          </button>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" data-testid="error-message">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="name-input"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                placeholder="chef@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="email-input"
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number (Optional)
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="phone-input"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                placeholder="Enter a strong password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                data-testid="password-input"
              />
            </div>
            
            {/* Chef-specific fields */}
            {signupType === 'chef' && (
              <div>
                <label htmlFor="safeserv" className="block text-sm font-medium text-gray-700 mb-1">
                  SafeServ Certification Number <span className="text-red-600">*</span>
                </label>
                <input
                  id="safeserv"
                  name="safeserv_cert_number"
                  type="text"
                  required
                  className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  placeholder="Enter your SafeServ certification number"
                  value={formData.safeserv_cert_number}
                  onChange={(e) => setFormData({ ...formData, safeserv_cert_number: e.target.value })}
                  data-testid="safeserv-input"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Required for all chefs. Upload certificate document after signup.
                </p>
              </div>
            )}

            {/* Bartender-specific fields */}
            {signupType === 'bartender' && (
              <>
                <div>
                  <label htmlFor="license" className="block text-sm font-medium text-gray-700 mb-1">
                    Bartender License <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="license"
                    name="license_file"
                    type="file"
                    required
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setLicenseFile(e.target.files[0])}
                    className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    data-testid="license-input"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Upload your bartending license (PDF, JPG, or PNG)
                  </p>
                </div>

                <div>
                  <label htmlFor="llc" className="block text-sm font-medium text-gray-700 mb-1">
                    LLC Document <span className="text-gray-400">(Optional)</span>
                  </label>
                  <input
                    id="llc"
                    name="llc_file"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setLlcFile(e.target.files[0])}
                    className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    data-testid="llc-input"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Upload your LLC documentation if applicable
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-900 font-semibold mb-1">💰 Subscription: $30/month</p>
                  <p className="text-xs text-purple-800">
                    • Bookings: $26 (2hr) or $53 (4hr) operational fee<br/>
                    • Must subscribe to start accepting bookings<br/>
                    • License will be verified by admin
                  </p>
                </div>
              </>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                signupType === 'chef'
                  ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  : 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="signup-submit-button"
            >
              {loading ? 'Creating account...' : `Sign up as ${signupType === 'chef' ? 'Chef' : 'Bartender'}`}
            </button>
          </div>
          
          <div className="text-center text-sm">
            <span className="text-gray-600">Already have an account? </span>
            <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChefSignUp;
